import { useMemo } from 'react';

import { useFiltersContext } from '../../../../../contexts/FilterContext';
import { getFiltersToApplyByCustomFilter } from '../../../../../contexts/FilterContext/hooks/utils';
import { compareFilterInputs, getCustomFilterPayload } from './customFilterPayloadUtils';

/**
 * Derives whether the current filter state has drifted from the selected custom filter's payload.
 *
 * This is a pure derivation with no stored state — safe to call from multiple components.
 * Returns `false` when no custom filter is selected.
 *
 * Approach: hydrate the stored custom filter payload into GlobalFilters (same pipeline used
 * when applying), then serialize both baseline and current through `getCustomFilterPayload`
 * so they share the same shape. This avoids __typename / key-count mismatches.
 */
export function useIsFilterDrifted(): boolean {
  const { filters, selectedCustomFilter } = useFiltersContext();

  return useMemo(() => {
    if (!selectedCustomFilter?.value) return false;

    const baselineFilters = getFiltersToApplyByCustomFilter(selectedCustomFilter, filters);

    const baselineSerialized = getCustomFilterPayload(selectedCustomFilter.id!, baselineFilters);
    const currentSerialized = getCustomFilterPayload(selectedCustomFilter.id!, filters);

    return !compareFilterInputs(currentSerialized, baselineSerialized);
  }, [filters, selectedCustomFilter]);
}
